import { Controller } from "@hotwired/stimulus"

export default class WebPushController extends Controller {
  static values = {
    vapidKey: String,
    subscribeUrl: String,
  }

  static targets = ["status", "enable", "disable"]

  declare vapidKeyValue: string
  declare subscribeUrlValue: string
  declare readonly statusTarget: HTMLElement
  declare readonly enableTarget: HTMLButtonElement
  declare readonly disableTarget: HTMLButtonElement

  async connect(): Promise<void> {
    await this.refresh()
  }

  async enable(): Promise<void> {
    if (!this.supported()) {
      this.setStatus("not-supported")
      return
    }

    this.enableTarget.disabled = true
    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.setStatus("denied")
        return
      }

      const registration = await navigator.serviceWorker.register("/web-push-sw.js")
      await registration.update()
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.vapidKeyValue,
      })

      const response = await fetch(this.subscribeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      })

      if (!response.ok) {
        this.setStatus("error")
        return
      }

      this.setStatus("on")
    } catch {
      this.setStatus("error")
    } finally {
      this.enableTarget.disabled = false
      await this.refresh()
    }
  }

  async disable(): Promise<void> {
    this.disableTarget.disabled = true
    try {
      const registration = await navigator.serviceWorker.getRegistration("/web-push-sw.js")
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        await fetch(this.subscribeUrlValue, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": this.csrfToken(),
          },
          body: JSON.stringify({ subscription: { endpoint: subscription.endpoint } }),
        })
        await subscription.unsubscribe()
      }
      this.setStatus("off")
    } catch {
      this.setStatus("error")
    } finally {
      this.disableTarget.disabled = false
      await this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    if (!this.vapidKeyValue) {
      this.setStatus("unconfigured")
      return
    }
    if (!this.supported()) {
      this.setStatus("not-supported")
      return
    }

    const registration = await navigator.serviceWorker.getRegistration("/web-push-sw.js")
    const subscription = await registration?.pushManager.getSubscription()
    this.setStatus(subscription ? "on" : "off")
  }

  private supported(): boolean {
    const secure = window.isSecureContext
    return secure && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  private setStatus(state: "on" | "off" | "denied" | "error" | "not-supported" | "unconfigured"): void {
    const labels: Record<typeof state, string> = {
      on: this.statusTarget.dataset.onLabel ?? "",
      off: this.statusTarget.dataset.offLabel ?? "",
      denied: this.statusTarget.dataset.deniedLabel ?? "",
      error: this.statusTarget.dataset.errorLabel ?? "",
      "not-supported": this.statusTarget.dataset.unsupportedLabel ?? "",
      unconfigured: this.statusTarget.dataset.unconfiguredLabel ?? "",
    }
    this.statusTarget.textContent = labels[state]
    const canUse = state === "on" || state === "off" || state === "denied" || state === "error"
    this.enableTarget.hidden = !canUse || state === "on"
    this.disableTarget.hidden = state !== "on"
    this.enableTarget.disabled = state === "unconfigured" || state === "not-supported"
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
  }
}
